/**
 * AI 상세 이미지 모달 — 상세설정의 일정 데이터·상세정보 글·상품 사진으로 쇼핑몰 상세페이지용 긴 세로 이미지를 만들어 콘텐츠로 붙인다.
 *
 * 흐름 (3단계):
 *   1) 내용     — 일정·상세정보 글(모든 구역: 투어소개·판매정보·이용안내·예약안내·유의사항·취소/환불·특전·호텔 등)·사진 썸네일을
 *               erp-video 에 보내 카피(제목·스펙 타일·상품 정보(판매정보)·포인트·특별 혜택·일차 요약·숙박 안내·포함/불포함·구역별 이용 안내)와
 *               사진 선택, 경로 좌표(route)를 받는다. 글은 전부 사람이 고칠 수 있다(경로는 서버가 준 대로).
 *   2) 미리보기 — 고른 테마·강조색으로 HTML 템플릿을 채우고 html2canvas 로 그려 보여 준다. 다시 그리는 데 비용이 없다.
 *               강조색 '자동' 이면 히어로 사진에서 색을 뽑고(extractAccentColor), 경로가 2곳 이상이면 경로 지도 PNG 를
 *               만들어(renderRouteMapImage, Google Static Maps 배경) 지도 섹션에 넣는다. 지도는 경로별로 캐시해 테마만 바꿀 때 다시 받지 않는다.
 *   3) 저장     — 다운로드하거나, 새 콘텐츠를 만들어 이미지를 첨부파일로 올리고 본문에 <img> 를 넣어 저장한다.
 *
 * 하이브리드 방식: AI 는 글과 사진 선택만 하고 그림은 브라우저가 고정 템플릿으로 그린다(detailImageRenderer.js).
 * AI 에게 상세 이미지를 통째로 그리게 하면 한국어 글자가 깨지고 실제와 다른 장소가 실리지만, 이 방식은 글자는 폰트가 그리고
 * 사진은 이 상품에 등록된 실제 사진만 쓴다. 사진이 없는 포인트만 선택적으로 AI 이미지(장당 ≈ $0.04)로 채울 수 있다.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { requestDetailCopy, generateImage, IMAGE_USD_PER_IMAGE, SCRIPT_TONES, usageLog, resetUsageLog, summarizeUsage } from '../services/ai/videoClient';
import { isAiConfigured } from '../services/ai/geminiClient';
import { makeThumbnailBase64 } from '../services/video/introVideoBuilder';
import { DETAIL_WIDTH, DETAIL_THEMES, DETAIL_ACCENTS, inlineImage, buildDetailHtml, renderDetailImage, stitchSlices, esc, extractAccentColor, renderRouteMapImage } from '../services/detailImage/detailImageRenderer';
import { buildProductPayload, extractContentExtras, visitOrderOf, collectPhotoCandidates, StepChip, Field, Notice, Spinner } from './AiIntroVideoModal';
import { createNewProductContent, uploadContentFile, saveProductContentInfo, getProductCity, getUserCompanyInfo } from '../services/apiService';
import { getUserInfo } from '../utils/tokenManager';

const S3_PUBLIC_PREFIX = 'https://kr.object.iwinv.kr/orgin/1/';
const DEFAULT_TITLE = 'AI 상세 이미지';
/** 템플릿(detailTemplate.buildDetailHtml showSections)의 섹션 키와 같다 */
const SECTION_LABELS = [
  { id: 'stats', label: '스펙 타일' },
  { id: 'map', label: '경로 지도' },
  { id: 'saleInfo', label: '상품 정보' },
  { id: 'highlights', label: '포인트' },
  { id: 'perks', label: '특별 혜택' },
  { id: 'days', label: '일정' },
  { id: 'hotels', label: '숙박 안내' },
  { id: 'includes', label: '포함·불포함' },
  { id: 'notices', label: '이용 안내' },
  { id: 'closing', label: '마무리' },
];
/** 상세정보 글 상한 — 상세 이미지는 모든 구역을 AI 에게 보내야 해서 소개영상보다 크게 (서버 handleDetailCopy 와 같게) */
const EXTRAS_OPTS = { max: 24, maxChars: 2500, totalChars: 16000, withArea: true };

const linesOf = (arr) => (Array.isArray(arr) ? arr : []).map((x) => String(x || '').trim()).filter(Boolean).join('\n');
const splitLines = (text) => String(text || '').split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
/** 생성 이미지 → data URL. mimeType 은 스타일 속성의 url() 안에 그대로 들어가므로 아는 값만 허용한다 */
const dataUrlOf = (img) => `data:${/^image\/(png|jpeg|webp)$/.test(img.mimeType) ? img.mimeType : 'image/png'};base64,${img.data}`;
/** 스펙 타일 최대 개수 (서버도 4개로 자른다) */
const MAX_STATS = 4;
/** 구역별 섹션 상한 (템플릿 계약·서버와 같게) */
const MAX_SALE_INFO = 8;
const MAX_HOTELS = 8;
const MAX_NOTICE_GROUPS = 6;

/**
 * AI 가 고른 photoIds 를 후보 URL 로 바꾸고, 못 고른 곳은 기계적으로 채운다.
 *   hero ← AI 선택 → 대표이미지 첫 장 → 아무 사진
 *   day  ← AI 선택 → 그 날의 카드 사진(아직 안 쓴 것)
 *   highlight ← AI 선택 → 아직 안 쓴 사진을 돌아가며 (없으면 빈 채로 두고 AI 이미지 생성 버튼을 보여 준다)
 * 한 장씩만 둔다(배열은 나중을 위해). 같은 사진이 여러 곳에 실리지 않게 used 로 거른다.
 */
function assignPhotos(raw, candidates) {
  const byId = new Map(candidates.map((c) => [c.id, c.url]));
  const used = new Set();
  const take = (ids) => {
    const u = (Array.isArray(ids) ? ids : []).map((id) => byId.get(Number(id))).find((x) => x && !used.has(x));
    if (u) used.add(u);
    return u ? [u] : [];
  };
  const firstFree = (pred = () => true) => {
    const c = candidates.find((x) => !used.has(x.url) && pred(x));
    if (c) used.add(c.url);
    return c ? [c.url] : [];
  };
  // 1차: AI 가 고른 사진을 모든 섹션에 먼저 배정한다. 폴백을 섹션 순서대로 섞어 돌리면 앞 섹션의 폴백이
  // 뒤 섹션이 고른 사진을 먼저 가져가 AI 의 선택이 조용히 사라진다.
  let heroUrls = take(raw.heroPhotoIds);
  const days = (Array.isArray(raw.days) ? raw.days : []).map((d) => ({
    day: Number(d.day) || 0, title: String(d.title || ''), summary: String(d.summary || ''), imageUrls: take(d.photoIds),
  }));
  const highlights = (Array.isArray(raw.highlights) ? raw.highlights : []).map((h) => ({
    title: String(h.title || ''), desc: String(h.desc || ''), imagePrompt: String(h.imagePrompt || '').trim(), imageUrls: take(h.photoIds),
  }));
  // 2차: 비어 있는 곳만 기계적으로 채운다
  if (!heroUrls.length) heroUrls = firstFree((c) => c.day === 0);
  if (!heroUrls.length) heroUrls = firstFree();
  for (const d of days) if (!d.imageUrls.length) d.imageUrls = firstFree((c) => c.day === d.day);
  for (const h of highlights) {
    if (h.imageUrls.length) continue;
    const pick = firstFree((c) => c.day !== 0); // 일정 사진 먼저, 없으면 남은 대표이미지
    h.imageUrls = pick.length ? pick : firstFree();
  }
  return { heroUrls, days, highlights };
}

export default function AiDetailImageModal({
  open,
  onClose,
  productId,
  productName,
  itineraryData,
  cardImagesByPlace,
  cardImagesNoPlace,
  cardImageOriginals, // 화면용 축소본 URL → 원본 URL
  repImages,
  contentMain, // 콘텐츠 목록 [{ 코드, 코드명, 컨텐츠 }] — 종류 선택 + 카피 근거 글 추출에 쓴다
  googleMapsKey, // 경로 지도 배경(Google Static Maps)용. 없으면 점·선만 그리는 약도
  onAttached, // 콘텐츠 저장 후 목록 새로고침용
}) {
  const [step, setStep] = useState('copy'); // 'copy' | 'preview' | 'done'
  const [working, setWorking] = useState(false); // 카피 요청·이미지 준비·렌더처럼 오래 걸리는 작업 중
  const [progress, setProgress] = useState(null); // { stage, done, total }
  const [error, setError] = useState('');
  // AI 카피(사용자가 고친 것). includes/excludes/perks/notes 와 notices[].items 는 textarea 편집용으로 "한 줄에 하나" 문자열로 든다
  const [copy, setCopy] = useState(null);
  const [contact, setContact] = useState('');
  const [imagePickerFor, setImagePickerFor] = useState(null); // 'hero' | 'h0' | 'd2' …
  const [generatingFor, setGeneratingFor] = useState(null); // AI 이미지 생성 중인 곳 (키 또는 'all')
  // 설정
  const [tone, setTone] = useState('calm');
  const [aiPickPhotos, setAiPickPhotos] = useState(true);
  const extrasAll = useMemo(() => extractContentExtras(contentMain, EXTRAS_OPTS), [contentMain]);
  // 근거 글 체크리스트를 구역별로 묶는다 (순서는 extrasAll 그대로, 처음 나오는 구역 순)
  const extrasByArea = useMemo(() => {
    const groups = [];
    extrasAll.forEach((e, i) => {
      const area = e.area || '기타';
      let g = groups.find((x) => x.area === area);
      if (!g) { g = { area, entries: [] }; groups.push(g); }
      g.entries.push({ e, i });
    });
    return groups;
  }, [extrasAll]);
  const [extrasOff, setExtrasOff] = useState(() => new Set());
  const visitOrder = useMemo(() => visitOrderOf(itineraryData), [itineraryData]);
  const [useVisitOrder, setUseVisitOrder] = useState(true);
  // 템플릿
  const [theme, setTheme] = useState('modern');
  const [accent, setAccent] = useState('auto'); // 강조색 id 또는 'auto'(히어로 사진에서 뽑음)
  const [resolvedAccent, setResolvedAccent] = useState(null); // 'auto' 일 때 실제로 쓴 '#rrggbb' (미리보기 칩 표시용)
  const [format, setFormat] = useState('jpeg');
  const [showSections, setShowSections] = useState(() => Object.fromEntries(SECTION_LABELS.map((s) => [s.id, true])));
  // 결과
  // 렌더 결과. 조각(slice)마다 blob·url — 긴 이미지는 캔버스 상한 때문에 섹션 단위로 나눠 그린다(renderDetailImage 주석)
  const [result, setResult] = useState(null); // { slices:[{ blob, url, width, height }], width, height, mimeType, scale, missing }
  const revokeResult = (r) => { for (const sl of r?.slices || []) if (sl.url) URL.revokeObjectURL(sl.url); };
  const [attachTypeCode, setAttachTypeCode] = useState('');
  const [attachTitle, setAttachTitle] = useState(DEFAULT_TITLE);
  const [attaching, setAttaching] = useState(false);
  const [usage, setUsage] = useState(() => summarizeUsage([]));
  const refreshUsage = useCallback(() => setUsage(summarizeUsage(usageLog)), []);

  const abortRef = useRef(null);
  const imageCacheRef = useRef(new Map()); // url → ImageBitmap (썸네일·인라인 공용)
  const inlineCacheRef = useRef(new Map()); // url → data URL (렌더용 인라인 결과. 테마만 바꿔 다시 그릴 때 다시 받지 않게)
  const mapCacheRef = useRef(new Map()); // JSON(route) → 경로 지도 data URL (테마·강조색만 바꿀 때 지도를 다시 받지 않게)
  const productRef = useRef(null); // 마지막 카피 요청에 쓴 product 페이로드 — 렌더 때 일차별 방문지·식사·숙소(dayMeta)를 꺼낸다
  const cityRef = useRef('');

  const candidates = useMemo(
    () => collectPhotoCandidates({ itineraryData, cardImagesByPlace, cardImagesNoPlace, cardImageOriginals, repImages }),
    [itineraryData, cardImagesByPlace, cardImagesNoPlace, cardImageOriginals, repImages],
  );
  // 렌더에는 원본 URL 을 쓴다 (화면·AI 썸네일은 가벼운 축소본). AI 생성 이미지(data URL)는 후보에 없으니 그대로.
  const hiResOf = useCallback((u) => candidates.find((c) => c.url === u)?.hiUrl || u, [candidates]);
  const contentTypes = useMemo(
    () => (contentMain || []).map((row) => ({ code: String(row?.코드 || ''), name: String(row?.코드명 || '') })).filter((t) => t.code),
    [contentMain],
  );
  // 열릴 때의 초기화 효과가 contentTypes 변경(첨부 후 목록 새로고침)에 다시 돌아 편집 중인 내용을 지우지 않게 ref 로 읽는다
  const contentTypesRef = useRef(contentTypes);
  contentTypesRef.current = contentTypes;

  // 모달이 열릴 때 초기화. 닫히면 진행 중이던 작업을 중단한다.
  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      return;
    }
    setStep('copy');
    setWorking(false);
    setProgress(null);
    setError('');
    setCopy(null);
    setResolvedAccent(null);
    productRef.current = null;
    setExtrasOff(new Set());
    setImagePickerFor(null);
    setGeneratingFor(null);
    resetUsageLog();
    setUsage(summarizeUsage([]));
    setResult((prev) => { revokeResult(prev); return null; });
    setAttachTypeCode(contentTypesRef.current?.[0]?.code || '');
    setAttachTitle(DEFAULT_TITLE);
    // 마무리 연락처: 회사 정보에서 채워 두고 사용자가 고칠 수 있게
    getUserCompanyInfo().then((c) => {
      if (!c) return;
      const phone = ['대표전화', '전화번호', '업체전화', '연락처', '휴대폰'].map((k) => c[k]).find((v) => typeof v === 'string' && v.trim());
      setContact([c.업체명, phone].filter(Boolean).join(' · '));
    }).catch(() => {});
  }, [open]);

  // 화면을 떠나면(언마운트) 진행 중이던 요청을 중단한다
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  // ── 1) 내용(카피) ────────────────────────────────────────────────
  const generateCopy = useCallback(async () => {
    setError('');
    setWorking(true);
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      if (!cityRef.current) {
        const city = await getProductCity({ productNo: productId }).catch(() => null);
        cityRef.current = city?.도시명 || city?.코드명 || '';
      }
      const extras = extrasAll.filter((_, i) => !extrasOff.has(i));
      const product = buildProductPayload({ productName, cityName: cityRef.current, itineraryData, contentMain, extras, useVisitOrder });
      if (!product.days.length) throw new Error('일정표에 등록된 일정이 없습니다. 일정을 먼저 등록해 주세요.');
      productRef.current = product;
      console.debug('[AI 상세이미지] 카피 근거', { days: product.days.length, visitOrder: product.keywords, extras: extras.map((e) => `${e.title} (${e.text.length}자)`) });

      // 사진 썸네일 — AI 가 보고 고르게. 실패한 사진은 빼고 보낸다.
      let photos = [];
      if (aiPickPhotos && candidates.length) {
        setProgress({ stage: 'photos', done: 0, total: candidates.length });
        let done = 0;
        photos = (await Promise.all(candidates.map(async (c) => {
          const data = await makeThumbnailBase64(c.url, imageCacheRef.current).catch(() => null);
          done++;
          setProgress({ stage: 'photos', done, total: candidates.length });
          return data ? { id: c.id, source: c.source, data } : null;
        }))).filter(Boolean);
        setProgress(null);
      }
      if (ac.signal.aborted) return;

      setProgress({ stage: 'copy' });
      const raw = await requestDetailCopy({ product, tone, photos, signal: ac.signal });
      refreshUsage();
      const { heroUrls, days, highlights } = assignPhotos(raw, candidates);
      setCopy({
        headline: String(raw.headline || productName || ''),
        subheadline: String(raw.subheadline || ''),
        badge: String(raw.badge || ''),
        // 경로 좌표는 서버(cleanRoute)가 거른 그대로 — 화면에서 고치지 않는다. 스펙 타일은 편집 가능.
        route: Array.isArray(raw.route) ? raw.route : [],
        stats: (Array.isArray(raw.stats) ? raw.stats : []).map((s) => ({ label: String(s?.label || ''), value: String(s?.value || '') })).slice(0, MAX_STATS),
        saleInfo: (Array.isArray(raw.saleInfo) ? raw.saleInfo : []).map((s) => ({ label: String(s?.label || ''), value: String(s?.value || '') })).filter((s) => s.label || s.value).slice(0, MAX_SALE_INFO),
        intro: String(raw.intro || ''),
        heroUrls,
        heroImagePrompt: String(raw.heroImagePrompt || '').trim(),
        highlights,
        perks: linesOf(raw.perks),
        days,
        hotels: (Array.isArray(raw.hotels) ? raw.hotels : []).map((h) => ({ name: String(h?.name || ''), note: String(h?.note || '') })).filter((h) => h.name || h.note).slice(0, MAX_HOTELS),
        includes: linesOf(raw.includes),
        excludes: linesOf(raw.excludes),
        notices: (Array.isArray(raw.notices) ? raw.notices : []).map((g) => ({ title: String(g?.title || ''), items: linesOf(g?.items) })).filter((g) => g.title || g.items).slice(0, MAX_NOTICE_GROUPS),
        notes: linesOf(raw.notes),
        closing: String(raw.closing || ''),
      });
    } catch (e) {
      if (e?.name !== 'AbortError') setError(e?.message || String(e));
    } finally {
      setWorking(false);
      setProgress(null);
    }
  }, [productId, productName, itineraryData, contentMain, extrasAll, extrasOff, useVisitOrder, candidates, aiPickPhotos, tone, refreshUsage]);

  const patchCopy = (patch) => setCopy((p) => (p ? { ...p, ...patch } : p));
  const patchHighlight = (i, patch) => setCopy((p) => (p ? { ...p, highlights: p.highlights.map((h, k) => (k === i ? { ...h, ...patch } : h)) } : p));
  const patchDay = (i, patch) => setCopy((p) => (p ? { ...p, days: p.days.map((d, k) => (k === i ? { ...d, ...patch } : d)) } : p));
  const patchStat = (i, patch) => setCopy((p) => (p ? { ...p, stats: p.stats.map((s, k) => (k === i ? { ...s, ...patch } : s)) } : p));
  const patchSaleInfo = (i, patch) => setCopy((p) => (p ? { ...p, saleInfo: p.saleInfo.map((s, k) => (k === i ? { ...s, ...patch } : s)) } : p));
  const patchHotel = (i, patch) => setCopy((p) => (p ? { ...p, hotels: p.hotels.map((h, k) => (k === i ? { ...h, ...patch } : h)) } : p));
  const patchNotice = (i, patch) => setCopy((p) => (p ? { ...p, notices: p.notices.map((g, k) => (k === i ? { ...g, ...patch } : g)) } : p));

  /** 사진 칸 키: 'hero' | 'h{i}'(포인트) | 'd{i}'(일차). 사진 스트립·선택창·AI 생성이 같은 코드로 다루게 */
  const urlsOf = (key) => {
    if (!copy) return [];
    if (key === 'hero') return copy.heroUrls;
    const i = Number(key.slice(1));
    return key[0] === 'h' ? copy.highlights[i]?.imageUrls || [] : copy.days[i]?.imageUrls || [];
  };
  const setUrls = useCallback((key, urls) => setCopy((p) => {
    if (!p) return p;
    if (key === 'hero') return { ...p, heroUrls: urls };
    const i = Number(key.slice(1));
    return key[0] === 'h'
      ? { ...p, highlights: p.highlights.map((h, k) => (k === i ? { ...h, imageUrls: urls } : h)) }
      : { ...p, days: p.days.map((d, k) => (k === i ? { ...d, imageUrls: urls } : d)) };
  }), []);

  // ── AI 이미지 생성 (사진이 없는 곳만, 유료) ───────────────────────
  const generateOne = useCallback(async (key, description, aspectRatio, signal) => {
    const desc = String(description || '').trim();
    if (!desc) throw new Error('이미지 설명(영어)이 비어 있습니다. 설명을 적은 뒤 생성하세요.');
    const img = await generateImage({ description: desc, aspectRatio, signal });
    refreshUsage();
    setUrls(key, [dataUrlOf(img)]);
  }, [refreshUsage, setUrls]);

  const generateFor = useCallback(async (key) => {
    if (!copy) return;
    setError('');
    setGeneratingFor(key);
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      if (key === 'hero') await generateOne('hero', copy.heroImagePrompt, '16:9', ac.signal);
      else await generateOne(key, copy.highlights[Number(key.slice(1))]?.imagePrompt, '4:3', ac.signal);
    } catch (e) {
      if (e?.name !== 'AbortError') setError(e?.message || String(e));
    } finally {
      setGeneratingFor(null);
    }
  }, [copy, generateOne]);

  /** 빈 곳(사진 없는 포인트 + 히어로)을 순서대로 전부 생성. 시작 전에 견적을 보여 주고 확인받는다. */
  const emptyKeys = useMemo(() => {
    if (!copy) return [];
    const keys = [];
    if (!copy.heroUrls.length) keys.push('hero');
    copy.highlights.forEach((h, i) => { if (!h.imageUrls.length) keys.push(`h${i}`); });
    return keys;
  }, [copy]);
  const generateAllEmpty = useCallback(async () => {
    if (!copy || !emptyKeys.length) return;
    const usd = IMAGE_USD_PER_IMAGE * emptyKeys.length;
    if (!window.confirm(`사진이 없는 ${emptyKeys.length}곳에 AI 이미지를 만듭니다. 예상 비용 약 $${usd.toFixed(2)} (장당 $${IMAGE_USD_PER_IMAGE.toFixed(2)}). 계속할까요?\n\nAI 이미지는 실제 장소와 다를 수 있으니 생성 후 확인해 주세요.`)) return;
    setError('');
    setGeneratingFor('all');
    const ac = new AbortController();
    abortRef.current = ac;
    const failed = [];
    try {
      for (const key of emptyKeys) {
        if (ac.signal.aborted) return;
        try {
          if (key === 'hero') await generateOne('hero', copy.heroImagePrompt, '16:9', ac.signal);
          else await generateOne(key, copy.highlights[Number(key.slice(1))]?.imagePrompt, '4:3', ac.signal);
        } catch (e) {
          if (e?.name === 'AbortError') return;
          failed.push(`${key === 'hero' ? '대표' : `포인트 ${Number(key.slice(1)) + 1}`}: ${e?.message || e}`);
        }
      }
      if (failed.length) setError(`일부 이미지를 만들지 못했습니다 — ${failed.join(' · ')}`);
    } finally {
      setGeneratingFor(null);
    }
  }, [copy, emptyKeys, generateOne]);

  // ── 2) 미리보기 (인라인 → 템플릿 → 렌더) ────────────────────────
  /** 지금 카피·설정으로 그린다. 사진은 data URL 로 인라인해 두고(캐시), 테마만 바꿀 때는 다시 받지 않는다. */
  const renderNow = useCallback(async (opts = {}) => {
    if (!copy) return;
    const th = opts.theme ?? theme;
    const ac0 = opts.accent ?? accent;
    setError('');
    setWorking(true);
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const urls = Array.from(new Set([
        ...copy.heroUrls,
        ...(showSections.highlights ? copy.highlights.flatMap((h) => h.imageUrls) : []),
        ...(showSections.days ? copy.days.flatMap((d) => d.imageUrls) : []),
      ].filter(Boolean)));
      // AI 생성 이미지(data URL)는 이미 인라인이라 캐시에 두 번 들지 않게 건너뛴다
      const todo = urls.filter((u) => !/^data:/i.test(u) && !inlineCacheRef.current.has(u));
      let done = 0;
      setProgress({ stage: 'inline', done: 0, total: todo.length });
      await Promise.all(todo.map(async (u) => {
        const data = await inlineImage(hiResOf(u), imageCacheRef.current).catch(() => null);
        // 실패는 캐시하지 않아 "다시 그리기" 때 한 번 더 시도한다 — loadImage 는 실패도 null 로 캐시하므로 그 항목을 지워 둔다
        if (data) inlineCacheRef.current.set(u, data);
        else imageCacheRef.current.delete(hiResOf(u));
        done++;
        setProgress({ stage: 'inline', done, total: todo.length });
      }));
      if (ac.signal.aborted) return;
      const inlined = (u) => (/^data:/i.test(u) ? u : inlineCacheRef.current.get(u));
      const inl = (arr) => arr.map(inlined).filter(Boolean);
      const missing = urls.filter((u) => !inlined(u)).length;
      const images = { hero: inl(copy.heroUrls), highlights: copy.highlights.map((h) => inl(h.imageUrls)), days: copy.days.map((d) => inl(d.imageUrls)), map: null };

      // 강조색 '자동': 히어로 사진에서 뽑는다. 사진이 없거나 실패하면 보라(기본)
      let accentOut = ac0;
      if (ac0 === 'auto') {
        const heroData = images.hero[0];
        const hex = heroData ? await extractAccentColor(heroData).catch(() => null) : null;
        accentOut = hex || 'violet';
        setResolvedAccent(hex || null);
      } else {
        setResolvedAccent(null);
      }
      if (ac.signal.aborted) return;

      // 경로 지도: 2곳 이상일 때만. 경로가 같으면 캐시(테마·강조색만 바꿀 때 Static Maps 를 다시 받지 않게)
      const route = Array.isArray(copy.route) ? copy.route : [];
      if (showSections.map && route.length >= 2) {
        const mapKey = JSON.stringify(route);
        if (!mapCacheRef.current.has(mapKey)) {
          setProgress({ stage: 'map' });
          const mapData = await renderRouteMapImage({ route, googleMapsKey, imageCache: imageCacheRef.current }).catch(() => null);
          if (mapData) mapCacheRef.current.set(mapKey, mapData);
        }
        images.map = mapCacheRef.current.get(mapKey) || null;
      }
      if (ac.signal.aborted) return;

      // 일차별 방문지·식사·숙소 — 카피를 만들 때 쓴 product 페이로드에서 그대로 (일차 카드의 칩·식사·숙소 줄)
      const dayMeta = {};
      for (const d of productRef.current?.days || []) {
        dayMeta[d.day] = { places: (d.places || []).map((p) => p.name).filter(Boolean), meals: d.meals || '', hotels: d.hotels || [] };
      }

      // 편집용 "한 줄에 하나" 문자열을 템플릿 계약(배열)으로 되돌린다
      const html = buildDetailHtml({
        copy: {
          ...copy,
          includes: splitLines(copy.includes),
          excludes: splitLines(copy.excludes),
          perks: splitLines(copy.perks),
          notices: (copy.notices || []).map((g) => ({ title: g.title, items: splitLines(g.items) })),
          notes: splitLines(copy.notes),
        },
        images,
        theme: th, accent: accentOut, contact, showSections, dayMeta,
      });
      setProgress({ stage: 'render', done: 0, total: 0 });
      const r = await renderDetailImage({ html, theme: th, format, signal: ac.signal, onProgress: (pr) => setProgress(pr) });
      setResult((prev) => { revokeResult(prev); return { ...r, slices: r.slices.map((sl) => ({ ...sl, url: URL.createObjectURL(sl.blob) })), missing }; });
      setStep('preview');
    } catch (e) {
      if (e?.name !== 'AbortError') setError(e?.message || String(e));
    } finally {
      setWorking(false);
      setProgress(null);
    }
  }, [copy, theme, accent, format, contact, showSections, hiResOf, googleMapsKey]);

  // ── 3) 저장 ─────────────────────────────────────────────────────
  const ext = result?.mimeType === 'image/png' ? 'png' : 'jpg';
  const totalBytes = result ? result.slices.reduce((n, sl) => n + sl.blob.size, 0) : 0;
  const sizeLabel = result ? `${result.width}×${result.height} px (×${result.scale}), ${ext.toUpperCase()} ${(totalBytes / 1024 / 1024).toFixed(1)}MB${result.slices.length > 1 ? ` · ${result.slices.length}장` : ''}` : '';

  const saveBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };
  /** 한 장으로 합쳐 내려받고, 캔버스 상한을 넘어 못 합치면 조각별로 번호를 붙여 내려받는다 */
  const download = async () => {
    if (!result) return;
    const base = `${(productName || '상품').replace(/[\\/:*?"<>|]/g, '_')}_상세이미지`;
    const stitched = result.slices.length === 1 ? result.slices[0] : await stitchSlices(result.slices, result.mimeType).catch(() => null);
    if (stitched) { saveBlob(stitched.blob, `${base}.${ext}`); return; }
    result.slices.forEach((sl, i) => setTimeout(() => saveBlob(sl.blob, `${base}_${String(i + 1).padStart(2, '0')}.${ext}`), i * 400));
  };

  const attachAsContent = useCallback(async () => {
    if (!result) return;
    setError('');
    setAttaching(true);
    try {
      const { companyNo, userNo } = getUserInfo() || {};
      const title = attachTitle.trim() || DEFAULT_TITLE;
      // 콘텐츠 편집기의 "일반 작성(제목 없이 본문만)" 과 같은 방식: 컨텐츠명을 빈칸(' ') 으로 저장하면 상세페이지에 왼쪽 제목 칸 없이 본문만 나온다.
      const PLAIN_TITLE = ' ';
      const created = await createNewProductContent({ productNo: productId, companyNo, userNo, contentTypeCode: attachTypeCode || '', title: PLAIN_TITLE });
      const contentNo = Number(created?.table?.[0]?.컨텐츠번호 || created?.Table?.[0]?.컨텐츠번호 || created?.contentNo || 0);
      if (!contentNo) throw new Error('콘텐츠를 만들지 못했습니다. 잠시 후 다시 시도해 주세요.');
      // 조각마다 파일을 올리고 본문에 세로로 이어 붙인다 (쇼핑몰 상세 이미지가 여러 장으로 올라가는 방식과 같다)
      const urls = [];
      for (let i = 0; i < result.slices.length; i++) {
        const sl = result.slices[i];
        const file = new File([sl.blob], `detail_${productId}_${String(i + 1).padStart(2, '0')}.${ext}`, { type: result.mimeType });
        const up = await uploadContentFile({ file, productNo: productId, contentNo });
        if (!up || up.error) throw new Error(`이미지 파일(${i + 1}/${result.slices.length}) 업로드에 실패했습니다. 파일이 크면 "다운로드" 로 받아 콘텐츠 편집의 파일 첨부로 직접 올려 주세요.`);
        urls.push(up.uploadResult?.url || up.uploadResult?.Url || `${S3_PUBLIC_PREFIX}${up.filePath}`);
      }
      // 폭은 렌더 폭에 맞춰 두어 상세페이지에서 더 커지지(흐려지지) 않게. display:block 이라 조각 사이에 틈이 없다
      // 일반 작성이라 왼쪽 제목 칸이 없으니, 제목은 본문 맨 위 한 줄로 넣는다.
      const html = `<div style="font-size: 18px; font-weight: 700; color: #222; margin: 0 0 10px; line-height: 1.4;">${esc(title)}</div>` + `<div style="max-width:${DETAIL_WIDTH}px;margin:0 auto;">${urls.map((u, i) => `<img src="${u}" alt="${esc(title)}${urls.length > 1 ? ` (${i + 1})` : ''}" style="width:100%;display:block;">`).join('')}</div>`;
      const url = urls[0];
      const saved = await saveProductContentInfo({
        productNo: productId, contentNo, companyNo, userNo, title: PLAIN_TITLE,
        text: `${title} (${DETAIL_WIDTH}×${Math.round(result.height / result.scale)}, ${ext.toUpperCase()}${urls.length > 1 ? `, ${urls.length}장` : ''})`,
        html, expose: true, dealerOnly: false, imageMappingUse: false, fileAttach: true,
      });
      if (saved?.error) throw new Error('콘텐츠 저장에 실패했습니다.');
      await onAttached?.({ contentNo, imageUrl: url });
      alert('상세 이미지가 콘텐츠로 첨부되었습니다.');
      onClose?.();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setAttaching(false);
    }
  }, [result, ext, attachTitle, attachTypeCode, productId, onAttached, onClose]);

  if (!open) return null;

  const busy = working || attaching || !!generatingFor;
  const pct = progress?.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const progressLabel = progress?.stage === 'photos' ? `사진 불러오는 중 ${progress.done}/${progress.total}`
    : progress?.stage === 'inline' ? `이미지 준비 중 ${progress.done}/${progress.total}`
      : progress?.stage === 'map' ? '경로 지도 만드는 중…'
        : progress?.stage === 'render' ? `이미지를 그리는 중…${progress.total ? ` ${progress.done}/${progress.total}` : ''}`
          : progress?.stage === 'copy' ? '일정과 사진을 보고 상세 문구를 쓰는 중입니다…' : '';

  const routeCount = copy?.route?.length || 0;
  const routeNames = (copy?.route || []).map((r) => r?.name).filter(Boolean);

  /** 강조색 스와치. 'auto' 는 그라데이션 원(히어로 사진에서 뽑음), 나머지는 단색. 내용·미리보기 두 곳에서 같은 UI 를 쓴다. */
  const accentSwatches = (size, onPick, disabled = false) => DETAIL_ACCENTS.map((a) => (
    <button
      key={a.id}
      type="button"
      title={a.id === 'auto' ? '히어로 사진에서 색을 뽑습니다' : a.label}
      disabled={disabled}
      className={`${size} rounded-full border-2 ${accent === a.id ? 'border-gray-800 scale-110' : 'border-white'}`}
      style={{ background: a.id === 'auto' ? 'conic-gradient(#6d28d9, #1d4ed8, #0f766e, #b45309, #be123c, #6d28d9)' : a.color }}
      onClick={() => onPick(a.id)}
    />
  ));

  /** 사진 스트립 + 선택창 + (선택) AI 생성 버튼. hero / 포인트 / 일차가 같은 UI 를 쓴다. (컴포넌트가 아니라 함수 — 렌더마다 새 컴포넌트가 되어 remount 되는 것을 피한다) */
  const photoStrip = (k, { max = 1, aiPrompt, aiAspect } = {}) => {
    const urls = urlsOf(k);
    const isGen = generatingFor === k || generatingFor === 'all';
    return (
      <div className="flex flex-wrap gap-1 items-center">
        {urls.map((u) => (
          <button key={u} type="button" title="이 사진 빼기" className="relative w-16 h-12 rounded overflow-hidden border group" onClick={() => setUrls(k, urls.filter((x) => x !== u))} disabled={busy}>
            <img src={u} alt="" className="w-full h-full object-cover" />
            <span className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-black/50 text-white text-xs">✕</span>
          </button>
        ))}
        {urls.length < max && candidates.length > 0 && (
          <div className="relative">
            <button type="button" className="w-16 h-12 rounded border border-dashed text-gray-500 text-lg leading-none hover:bg-white" title="사진 고르기" onClick={() => setImagePickerFor(imagePickerFor === k ? null : k)} disabled={busy}>＋</button>
            {imagePickerFor === k && (
              <div className="absolute z-20 left-0 top-full mt-1 bg-white border rounded-lg shadow-lg p-2 w-72 max-h-56 overflow-y-auto">
                <div className="grid grid-cols-4 gap-1">
                  {candidates.filter((c) => !urls.includes(c.url)).map((c) => (
                    <button key={c.url} type="button" title={c.source} className="aspect-video rounded overflow-hidden border hover:ring-2 ring-violet-500" onClick={() => { setUrls(k, [...urls, c.url].slice(0, max)); setImagePickerFor(null); }}>
                      <img src={c.url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {!urls.length && aiPrompt !== undefined && (
          <button type="button" className="px-2 py-1 text-[11px] rounded border border-violet-300 text-violet-700 bg-white hover:bg-violet-50 disabled:opacity-50" onClick={() => generateFor(k)} disabled={busy || !isAiConfigured()} title={`아래 영어 설명으로 ${aiAspect} 이미지를 만듭니다 (유료). 실제 장소와 다를 수 있습니다.`}>
            {isGen ? <><Spinner />만드는 중…</> : `AI 이미지 생성 (약 $${IMAGE_USD_PER_IMAGE.toFixed(2)})`}
          </button>
        )}
        {!urls.length && aiPrompt === undefined && <span className="text-[11px] text-gray-400">사진 없음 (이 일차는 사진 없이 글만 실립니다)</span>}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[94vh] flex flex-col overflow-hidden">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-violet-100 text-violet-700 text-xs font-bold">AI</span>
            상세 이미지 만들기
            <span className="text-xs font-normal text-gray-500">— 일정 데이터 + 상품 사진으로 상세페이지용 긴 이미지</span>
          </h3>
          <div className="flex items-center gap-2">
            <StepChip n={1} label="내용" active={step === 'copy'} done={step !== 'copy'} />
            <StepChip n={2} label="미리보기" active={step === 'preview'} done={step === 'done'} />
            <StepChip n={3} label="저장" active={step === 'done'} done={false} />
            <button type="button" className="ml-2 text-gray-400 hover:text-gray-700" onClick={() => onClose?.()} disabled={busy} title="닫기">✕</button>
          </div>
        </div>

        {/* 본문 */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!isAiConfigured() && (
            <Notice tone="warn">AI 연결 설정이 없습니다. .env 에 REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY / REACT_APP_ERP_APP_TOKEN 를 넣고 개발 서버를 다시 시작하세요.</Notice>
          )}
          {error && <Notice tone="error">{error}</Notice>}

          {/* ── 1) 내용 ── */}
          {step === 'copy' && (
            <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 items-start">
              {/* 설정 열 */}
              <div className="space-y-3 md:sticky md:top-0">
                <div className="border rounded-lg p-3 bg-gray-50 space-y-3">
                  <Field label="톤">
                    <select className="w-full border rounded px-2 py-1.5 text-sm" value={tone} onChange={(e) => setTone(e.target.value)} disabled={busy}>
                      {SCRIPT_TONES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                  </Field>
                  <label className="flex items-center gap-1 text-xs cursor-pointer" title="상품 사진 썸네일을 AI 에게 보여주고 대표·포인트·일차마다 어울리는 사진을 고르게 합니다">
                    <input type="checkbox" checked={aiPickPhotos} onChange={(e) => setAiPickPhotos(e.target.checked)} disabled={busy} />AI 가 사진을 고르게 ({candidates.length}장)
                  </label>
                  <div>
                    <div className="text-[11px] font-medium text-gray-600 mb-1">근거 데이터 — 일정표 {Array.isArray(itineraryData?.table1) ? new Set(itineraryData.table1.map((r) => Number(r?.일차)).filter((n) => n > 0)).size : 0}일 · 상세정보 글 {extrasAll.length - extrasOff.size}/{extrasAll.length}개</div>
                    {visitOrder.length > 0 && (
                      <label className="flex items-start gap-1 text-[11px] cursor-pointer mb-1" title="일정표 편집의 '주요 관광지 방문순서'. 카피의 방문지 힌트로 씁니다.">
                        <input type="checkbox" className="mt-0.5" checked={useVisitOrder} onChange={(e) => setUseVisitOrder(e.target.checked)} disabled={busy} />
                        <span className={useVisitOrder ? '' : 'text-gray-400 line-through'}><span className="font-medium">방문순서</span> {visitOrder.join(' → ')}</span>
                      </label>
                    )}
                    {extrasAll.length ? (
                      <div className="flex flex-col gap-0.5 max-h-52 overflow-y-auto" title="구역별로 묶여 있습니다. 끈 글은 AI 에게 보내지 않습니다.">
                        {extrasByArea.map((g) => (
                          <div key={g.area} className="mb-1">
                            <div className="text-[10px] font-semibold text-violet-700 uppercase tracking-wide">{g.area}</div>
                            {g.entries.map(({ e, i }) => (
                              <label key={`${e.title}-${i}`} className="flex items-center gap-1 text-[11px] cursor-pointer pl-2" title={e.text.slice(0, 300)}>
                                <input type="checkbox" checked={!extrasOff.has(i)} disabled={busy} onChange={(ev) => setExtrasOff((prev) => { const next = new Set(prev); if (ev.target.checked) next.delete(i); else next.add(i); return next; })} />
                                <span className={`truncate ${extrasOff.has(i) ? 'text-gray-400 line-through' : ''}`}>{e.item || e.title}</span>
                                <span className="text-gray-400 shrink-0">{e.text.length}자</span>
                              </label>
                            ))}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-gray-500">상세정보 콘텐츠에 글이 없어 일정표만으로 씁니다.</p>
                    )}
                  </div>
                  <button type="button" className="w-full px-3 py-2 text-sm rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50" onClick={generateCopy} disabled={busy || !isAiConfigured()}>
                    {working && (progress?.stage === 'photos' || progress?.stage === 'copy') ? <><Spinner />{progressLabel}</> : copy ? '내용 다시 만들기' : '내용 만들기'}
                  </button>
                </div>

                {/* 템플릿 — 카피와 무관하게 언제든 바꿀 수 있다 (미리보기에서도) */}
                <div className="border rounded-lg p-3 bg-gray-50 space-y-3">
                  <Field label="테마">
                    <select className="w-full border rounded px-2 py-1.5 text-sm" value={theme} title={DETAIL_THEMES.find((t) => t.id === theme)?.hint} onChange={(e) => setTheme(e.target.value)}>
                      {DETAIL_THEMES.map((t) => <option key={t.id} value={t.id} title={t.hint}>{t.label}</option>)}
                    </select>
                  </Field>
                  <div>
                    <div className="text-[11px] font-medium text-gray-600 mb-1">강조색 <span className="font-normal text-gray-400">(첫 번째는 사진에서 자동)</span></div>
                    <div className="flex gap-1.5">
                      {accentSwatches('w-7 h-7', setAccent)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium text-gray-600 mb-1">출력 형식</div>
                    <div className="flex gap-3 text-xs">
                      <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name="detail-format" checked={format === 'jpeg'} onChange={() => setFormat('jpeg')} />JPEG (권장, 작음)</label>
                      <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name="detail-format" checked={format === 'png'} onChange={() => setFormat('png')} />PNG</label>
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium text-gray-600 mb-1">섹션</div>
                    <div className="flex flex-col gap-0.5 text-xs">
                      {SECTION_LABELS.map((s) => {
                        const off = s.id === 'map' && routeCount < 2;
                        return (
                          <label key={s.id} className={`flex items-center gap-1 ${off ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer'}`} title={off ? '경로 좌표가 2곳 이상일 때만 지도 섹션을 넣을 수 있습니다' : undefined}>
                            <input type="checkbox" checked={!!showSections[s.id]} disabled={off} onChange={(e) => setShowSections((p) => ({ ...p, [s.id]: e.target.checked }))} />{s.label}
                          </label>
                        );
                      })}
                    </div>
                    {copy && (
                      <p className="mt-1 text-[11px] text-gray-500 leading-snug" title={routeNames.join(' → ')}>
                        {routeCount >= 2
                          ? `경로 지도: ${routeCount}곳 (${routeNames.join(' → ')})`
                          : '경로 좌표 없음 — 지도 섹션은 생략됩니다'}
                      </p>
                    )}
                  </div>
                  <Field label="마무리 연락처">
                    <input className="w-full border rounded px-2 py-1.5 text-sm" value={contact} placeholder="회사명 · 전화" onChange={(e) => setContact(e.target.value)} />
                  </Field>
                </div>
              </div>

              {/* 편집 열 */}
              <div className="min-w-0">
                {!copy && !working && (
                  <div className="py-16 text-center border-2 border-dashed rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">왼쪽에서 톤과 근거 데이터를 정한 뒤 "내용 만들기"를 누르세요.</p>
                    <p className="text-[11px] text-gray-500">AI 는 제목·스펙 타일·상품 정보(판매정보)·소개·포인트 3~4개·특별 혜택·일차 요약·숙박 안내·포함/불포함·구역별 이용 안내(예약·유의사항·취소/환불)를 쓰고 어울리는 사진과 경로 지도 좌표를 고릅니다. 사진은 이 상품에 등록된 실제 사진만 씁니다.</p>
                  </div>
                )}
                {!copy && working && (
                  <div className="py-16 text-center text-sm text-gray-600"><Spinner />{progressLabel}</div>
                )}
                {copy && (
                  <div className="space-y-4">
                    {/* 대표(히어로) */}
                    <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
                      <div className="text-xs font-semibold text-gray-700">대표 영역</div>
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-2">
                        <input className="w-full border rounded px-2 py-1.5 text-sm font-medium" value={copy.headline} placeholder="큰 제목" onChange={(e) => patchCopy({ headline: e.target.value })} />
                        <input className="w-full border rounded px-2 py-1.5 text-sm" value={copy.badge} placeholder="배지 (예: 3박 5일)" onChange={(e) => patchCopy({ badge: e.target.value })} />
                      </div>
                      <input className="w-full border rounded px-2 py-1.5 text-sm" value={copy.subheadline} placeholder="부제 (한 줄)" onChange={(e) => patchCopy({ subheadline: e.target.value })} />
                      {photoStrip('hero', { aiPrompt: copy.heroImagePrompt, aiAspect: '16:9' })}
                      {!copy.heroUrls.length && (
                        <textarea className="w-full border rounded px-2 py-1 text-[11px] leading-snug font-mono" rows={2} maxLength={600} value={copy.heroImagePrompt} placeholder="대표 이미지 설명 (영어)" onChange={(e) => patchCopy({ heroImagePrompt: e.target.value })} />
                      )}
                    </div>

                    {/* 스펙 타일 — 히어로 아래 짧은 사실(기간·항공·숙박…) 최대 4개 */}
                    <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold text-gray-700">스펙 타일 ({copy.stats.length}/{MAX_STATS}){!showSections.stats && <span className="ml-1 text-gray-400 font-normal">— 섹션 꺼짐</span>}</div>
                        {copy.stats.length < MAX_STATS && (
                          <button type="button" className="text-[11px] text-violet-700 hover:underline disabled:opacity-50" onClick={() => patchCopy({ stats: [...copy.stats, { label: '', value: '' }] })} disabled={busy}>+ 추가</button>
                        )}
                      </div>
                      {copy.stats.length ? copy.stats.map((s, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input className="w-24 border rounded px-2 py-1 text-sm" value={s.label} placeholder="항목 (예: 항공)" maxLength={8} onChange={(e) => patchStat(i, { label: e.target.value })} />
                          <input className="flex-1 min-w-0 border rounded px-2 py-1 text-sm" value={s.value} placeholder="값 (예: 대한항공 직항)" maxLength={24} onChange={(e) => patchStat(i, { value: e.target.value })} />
                          <button type="button" className="text-[11px] text-gray-400 hover:text-red-600" title="이 타일 빼기" onClick={() => patchCopy({ stats: copy.stats.filter((_, k) => k !== i) })} disabled={busy}>✕</button>
                        </div>
                      )) : (
                        <p className="text-[11px] text-gray-500">타일이 없습니다. "+ 추가" 로 기간·항공·숙박 같은 짧은 사실을 넣을 수 있습니다.</p>
                      )}
                    </div>

                    <Field label="소개 문단">
                      <textarea className="w-full border rounded px-2 py-1.5 text-sm leading-relaxed" rows={3} value={copy.intro} onChange={(e) => patchCopy({ intro: e.target.value })} />
                    </Field>

                    {/* 포인트 */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold text-gray-700">이 여행의 포인트 ({copy.highlights.length}개){!showSections.highlights && <span className="ml-1 text-gray-400 font-normal">— 섹션 꺼짐</span>}</div>
                        {emptyKeys.length > 0 && (
                          <button type="button" className="text-[11px] text-violet-700 hover:underline disabled:opacity-50" onClick={generateAllEmpty} disabled={busy || !isAiConfigured()} title="사진이 없는 대표·포인트 칸을 AI 이미지로 한 번에 채웁니다 (유료)">
                            {generatingFor === 'all' ? '빈 곳 만드는 중…' : `빈 곳 모두 생성 (${emptyKeys.length}장 ≈ $${(IMAGE_USD_PER_IMAGE * emptyKeys.length).toFixed(2)})`}
                          </button>
                        )}
                      </div>
                      {copy.highlights.map((h, i) => (
                        <div key={i} className="border rounded-lg p-3 bg-gray-50 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-semibold text-gray-500 w-6">{String(i + 1).padStart(2, '0')}</span>
                            <input className="flex-1 border rounded px-2 py-1 text-sm font-medium" value={h.title} placeholder="포인트 제목" onChange={(e) => patchHighlight(i, { title: e.target.value })} />
                            <button type="button" className="text-[11px] text-gray-400 hover:text-red-600" title="이 포인트 빼기" onClick={() => patchCopy({ highlights: copy.highlights.filter((_, k) => k !== i) })} disabled={busy}>빼기</button>
                          </div>
                          <textarea className="w-full border rounded px-2 py-1 text-sm leading-relaxed" rows={2} value={h.desc} placeholder="설명" onChange={(e) => patchHighlight(i, { desc: e.target.value })} />
                          {photoStrip(`h${i}`, { aiPrompt: h.imagePrompt, aiAspect: '4:3' })}
                          {!h.imageUrls.length && (
                            <textarea className="w-full border rounded px-2 py-1 text-[11px] leading-snug font-mono" rows={2} maxLength={600} value={h.imagePrompt} placeholder="이미지 설명 (영어) — 생성 전에 고칠 수 있습니다" onChange={(e) => patchHighlight(i, { imagePrompt: e.target.value })} />
                          )}
                        </div>
                      ))}
                    </div>

                    {/* 일차 */}
                    <div className="space-y-2">
                      <div className="text-xs font-semibold text-gray-700">일정 한눈에 ({copy.days.length}일){!showSections.days && <span className="ml-1 text-gray-400 font-normal">— 섹션 꺼짐</span>}</div>
                      {copy.days.map((d, i) => (
                        <div key={i} className="border rounded-lg p-3 bg-gray-50 flex gap-3">
                          <div className="shrink-0">
                            <span className="inline-block px-2 py-0.5 rounded-full bg-violet-600 text-white text-[11px] font-bold">DAY {d.day}</span>
                            <div className="mt-2">{photoStrip(`d${i}`)}</div>
                          </div>
                          <div className="flex-1 min-w-0 space-y-1.5">
                            <input className="w-full border rounded px-2 py-1 text-sm font-medium" value={d.title} placeholder="그 날의 제목" onChange={(e) => patchDay(i, { title: e.target.value })} />
                            <textarea className="w-full border rounded px-2 py-1 text-sm leading-relaxed" rows={2} value={d.summary} placeholder="요약" onChange={(e) => patchDay(i, { summary: e.target.value })} />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* 포함·불포함·유의사항 — 한 줄에 하나 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Field label={`포함 (한 줄에 하나)${showSections.includes ? '' : ' — 섹션 꺼짐'}`}>
                        <textarea className="w-full border rounded px-2 py-1 text-sm leading-relaxed" rows={4} value={copy.includes} onChange={(e) => patchCopy({ includes: e.target.value })} />
                      </Field>
                      <Field label="불포함 (한 줄에 하나)">
                        <textarea className="w-full border rounded px-2 py-1 text-sm leading-relaxed" rows={4} value={copy.excludes} onChange={(e) => patchCopy({ excludes: e.target.value })} />
                      </Field>
                    </div>

                    {/* 상품 정보 한눈에 — 판매정보 구역의 라벨/값 (출발일·항공·최소 출발·인솔자·호텔 등급…) 최대 8개 */}
                    <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold text-gray-700">상품 정보 한눈에 (판매정보) ({copy.saleInfo.length}/{MAX_SALE_INFO}){!showSections.saleInfo && <span className="ml-1 text-gray-400 font-normal">— 섹션 꺼짐</span>}</div>
                        {copy.saleInfo.length < MAX_SALE_INFO && (
                          <button type="button" className="text-[11px] text-violet-700 hover:underline disabled:opacity-50" onClick={() => patchCopy({ saleInfo: [...copy.saleInfo, { label: '', value: '' }] })} disabled={busy}>+ 추가</button>
                        )}
                      </div>
                      {copy.saleInfo.length ? copy.saleInfo.map((s, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input className="w-24 border rounded px-2 py-1 text-sm" value={s.label} placeholder="항목 (예: 출발일)" maxLength={8} onChange={(e) => patchSaleInfo(i, { label: e.target.value })} />
                          <input className="flex-1 min-w-0 border rounded px-2 py-1 text-sm" value={s.value} placeholder="값 (예: 매주 화·토 출발)" maxLength={40} onChange={(e) => patchSaleInfo(i, { value: e.target.value })} />
                          <button type="button" className="text-[11px] text-gray-400 hover:text-red-600" title="이 항목 빼기" onClick={() => patchCopy({ saleInfo: copy.saleInfo.filter((_, k) => k !== i) })} disabled={busy}>✕</button>
                        </div>
                      )) : (
                        <p className="text-[11px] text-gray-500">판매정보 구역에서 찾은 사실이 없습니다. "+ 추가" 로 출발일·항공·최소 출발 인원 같은 항목을 넣을 수 있습니다.</p>
                      )}
                    </div>

                    <Field label={`특별 혜택 (한 줄에 하나)${showSections.perks ? '' : ' — 섹션 꺼짐'}`}>
                      <textarea className="w-full border rounded px-2 py-1 text-sm leading-relaxed" rows={3} value={copy.perks} placeholder="특전·혜택 구역의 항목. 비우면 섹션이 생략됩니다." onChange={(e) => patchCopy({ perks: e.target.value })} />
                    </Field>

                    {/* 숙박 안내 — 호텔명 + 한 줄 설명(도시·박수·등급·조식) 최대 8개 */}
                    <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold text-gray-700">숙박 안내 ({copy.hotels.length}/{MAX_HOTELS}){!showSections.hotels && <span className="ml-1 text-gray-400 font-normal">— 섹션 꺼짐</span>}</div>
                        {copy.hotels.length < MAX_HOTELS && (
                          <button type="button" className="text-[11px] text-violet-700 hover:underline disabled:opacity-50" onClick={() => patchCopy({ hotels: [...copy.hotels, { name: '', note: '' }] })} disabled={busy}>+ 추가</button>
                        )}
                      </div>
                      {copy.hotels.length ? copy.hotels.map((h, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input className="w-44 border rounded px-2 py-1 text-sm font-medium" value={h.name} placeholder="호텔명" maxLength={40} onChange={(e) => patchHotel(i, { name: e.target.value })} />
                          <input className="flex-1 min-w-0 border rounded px-2 py-1 text-sm" value={h.note} placeholder="예: 밀라노 2박 · 4성급 · 조식 포함" maxLength={80} onChange={(e) => patchHotel(i, { note: e.target.value })} />
                          <button type="button" className="text-[11px] text-gray-400 hover:text-red-600" title="이 호텔 빼기" onClick={() => patchCopy({ hotels: copy.hotels.filter((_, k) => k !== i) })} disabled={busy}>✕</button>
                        </div>
                      )) : (
                        <p className="text-[11px] text-gray-500">숙박 정보가 없습니다. "+ 추가" 로 호텔을 넣을 수 있습니다.</p>
                      )}
                    </div>

                    {/* 이용 안내 — 구역별 그룹(이용안내·예약안내·유의사항·취소/환불). 그룹이 없으면 옛 평면 목록(notes) 편집칸을 보여 준다 */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold text-gray-700">이용 안내 ({copy.notices.length}/{MAX_NOTICE_GROUPS} 그룹){!showSections.notices && <span className="ml-1 text-gray-400 font-normal">— 섹션 꺼짐</span>}</div>
                        {copy.notices.length < MAX_NOTICE_GROUPS && (
                          <button type="button" className="text-[11px] text-violet-700 hover:underline disabled:opacity-50" onClick={() => patchCopy({ notices: [...copy.notices, { title: '', items: '' }] })} disabled={busy}>+ 그룹 추가</button>
                        )}
                      </div>
                      {copy.notices.map((g, i) => (
                        <div key={i} className="border rounded-lg p-3 bg-gray-50 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <input className="flex-1 border rounded px-2 py-1 text-sm font-medium" value={g.title} placeholder="그룹 제목 (예: 취소/환불 규정)" maxLength={20} onChange={(e) => patchNotice(i, { title: e.target.value })} />
                            <button type="button" className="text-[11px] text-gray-400 hover:text-red-600" title="이 그룹 빼기" onClick={() => patchCopy({ notices: copy.notices.filter((_, k) => k !== i) })} disabled={busy}>✕</button>
                          </div>
                          <textarea className="w-full border rounded px-2 py-1 text-sm leading-relaxed" rows={3} value={g.items} placeholder="안내 문장 (한 줄에 하나, 최대 10개)" onChange={(e) => patchNotice(i, { items: e.target.value })} />
                        </div>
                      ))}
                      {!copy.notices.length && (
                        <Field label="예약 안내 (간단) — 한 줄에 하나">
                          <textarea className="w-full border rounded px-2 py-1 text-sm leading-relaxed" rows={3} value={copy.notes} placeholder="구역별 그룹이 없을 때 쓰는 간단 목록. 비우면 섹션이 생략됩니다." onChange={(e) => patchCopy({ notes: e.target.value })} />
                        </Field>
                      )}
                    </div>

                    <Field label={`마무리 문구${showSections.closing ? '' : ' — 섹션 꺼짐'}`}>
                      <input className="w-full border rounded px-2 py-1.5 text-sm" value={copy.closing} onChange={(e) => patchCopy({ closing: e.target.value })} />
                    </Field>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── 2) 미리보기 ── */}
          {step === 'preview' && result && (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <select className="border rounded px-2 py-1 text-xs" value={theme} onChange={(e) => { setTheme(e.target.value); renderNow({ theme: e.target.value }); }} disabled={working}>
                    {DETAIL_THEMES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  <div className="flex gap-1">
                    {accentSwatches('w-6 h-6', (id) => { setAccent(id); renderNow({ accent: id }); }, working)}
                  </div>
                  {accent === 'auto' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full border bg-white text-gray-600" title="히어로 사진에서 뽑은 강조색">
                      자동 색상
                      <span className="inline-block w-3 h-3 rounded-sm border" style={{ background: resolvedAccent || '#6d28d9' }} />
                      {resolvedAccent ? <span className="font-mono">{resolvedAccent}</span> : <span className="text-gray-400">사진 없음 → 보라</span>}
                    </span>
                  )}
                  <button type="button" className="px-2.5 py-1 text-xs rounded border bg-white hover:bg-gray-50 disabled:opacity-50" onClick={() => renderNow()} disabled={working}>{working ? <><Spinner />{progressLabel}</> : '다시 그리기'}</button>
                </div>
                <div className="text-xs text-gray-500">
                  {sizeLabel}
                  {result.missing > 0 && <span className="ml-2 text-amber-600">불러오지 못한 사진 {result.missing}장은 비워 두었습니다.</span>}
                </div>
              </div>
              <div className="border rounded-lg bg-gray-100 overflow-y-auto" style={{ maxHeight: '70vh' }}>
                <div className="mx-auto w-full max-w-[900px]">
                  {result.slices.map((sl, i) => <img key={i} src={sl.url} alt={`상세 이미지 미리보기 ${i + 1}`} className="block w-full" />)}
                </div>
              </div>
              <p className="text-[11px] text-gray-500">글·사진이 마음에 안 들면 "내용 수정"으로 돌아가세요. 테마·강조색은 여기서 바로 바꿔 볼 수 있습니다(비용 없음).</p>
            </div>
          )}

          {/* ── 3) 저장 ── */}
          {step === 'done' && result && (
            <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4 items-start">
              <div className="border rounded-lg p-2 bg-gray-50">
                <div className="overflow-hidden rounded border bg-white" style={{ maxHeight: '48vh' }}>
                  {result.slices.map((sl, i) => <img key={i} src={sl.url} alt="" className="block w-full" />)}
                </div>
                <div className="mt-1 text-[11px] text-gray-500 text-center">{sizeLabel}</div>
              </div>
              <div className="border rounded-lg p-3 bg-gray-50 space-y-3">
                <Field label="콘텐츠 종류">
                  <select className="w-full border rounded px-2 py-1.5 text-sm" value={attachTypeCode} onChange={(e) => setAttachTypeCode(e.target.value)}>
                    {contentTypes.map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
                    {!contentTypes.length && <option value="">(종류 없음)</option>}
                  </select>
                </Field>
                <Field label="콘텐츠 제목 (본문 맨 위에 들어갑니다)">
                  <input className="w-full border rounded px-2 py-1.5 text-sm" value={attachTitle} onChange={(e) => setAttachTitle(e.target.value)} />
                </Field>
                <div className="flex items-center gap-2">
                  <button type="button" className="px-3 py-1.5 text-sm rounded border bg-white hover:bg-gray-100" onClick={download} disabled={attaching}>다운로드 ({ext})</button>
                  <button type="button" className="px-4 py-1.5 text-sm rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50" onClick={attachAsContent} disabled={attaching}>
                    {attaching ? '첨부하는 중…' : '콘텐츠로 첨부'}
                  </button>
                </div>
                <p className="text-[11px] text-gray-500">"일반 작성(제목 없이 본문만)" 형태로 첨부됩니다 — 상세페이지에 왼쪽 제목 칸 없이 제목 한 줄과 이미지가 본문으로 나옵니다. 첨부하면 이 상품에 새 콘텐츠가 만들어지고, 이미지가 첨부파일로 올라간 뒤 본문에 이미지가 들어갑니다. 다운로드한 파일은 다른 판매 채널의 상세페이지에 그대로 올릴 수 있습니다.</p>
              </div>
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50">
          <div className="text-[11px] text-gray-500">
            {step === 'copy' && 'AI 는 글과 사진 선택만 합니다. 이미지는 이 브라우저에서 템플릿으로 그리므로 한국어 글자가 깨지지 않습니다.'}
            {step === 'preview' && `${DETAIL_WIDTH}px 폭 · 쇼핑몰 상세페이지 규격`}
            {step === 'done' && '마음에 안 들면 "미리보기" 나 "내용 수정" 으로 돌아가 다시 만들 수 있습니다.'}
            {(usage.script.calls > 0 || (usage.image?.calls || 0) > 0) && (
              <span className="block mt-0.5 text-gray-400" title="응답에 실려 온 usageMetadata 합계. 비용은 공개 단가로 계산한 추정치입니다.">
                이 작업 AI 사용량 — 카피 {usage.script.calls}회 (입력 {usage.script.input.toLocaleString()} · 출력 {usage.script.output.toLocaleString()} 토큰)
                {(usage.image?.calls || 0) > 0 && ` · 이미지 ${usage.image.calls}장`}
                {usage.usd != null ? ` ≈ 합계 $${usage.usd.toFixed(3)}` : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === 'copy' && (
              <>
                <button type="button" className="px-3 py-1.5 text-sm rounded border hover:bg-white" onClick={() => onClose?.()} disabled={busy}>닫기</button>
                <button type="button" className="px-4 py-1.5 text-sm rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50" onClick={() => renderNow()} disabled={!copy || busy}>
                  {working && copy ? <><Spinner />{progressLabel}</> : '미리보기 만들기 →'}
                </button>
              </>
            )}
            {step === 'preview' && (
              <>
                <button type="button" className="px-3 py-1.5 text-sm rounded border hover:bg-white" onClick={() => setStep('copy')} disabled={working}>← 내용 수정</button>
                <button type="button" className="px-4 py-1.5 text-sm rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50" onClick={() => setStep('done')} disabled={working || !result}>저장하기 →</button>
              </>
            )}
            {step === 'done' && (
              <>
                <button type="button" className="px-3 py-1.5 text-sm rounded border hover:bg-white" onClick={() => setStep('preview')} disabled={attaching}>← 미리보기</button>
                <button type="button" className="px-3 py-1.5 text-sm rounded border hover:bg-white" onClick={() => setStep('copy')} disabled={attaching}>내용 수정</button>
              </>
            )}
          </div>
        </div>
        {pct > 0 && working && <div className="h-0.5 bg-violet-600 transition-all" style={{ width: `${pct}%` }} />}
      </div>
    </div>
  );
}
